/**
 * Full-screen photo viewer of the dark rooms (design: dialog.viewer): swipe on phones, arrows on desktop.
 * Footer: heart (real gallery like), "Text me" (PIN -> Text, replying to this photo), comments (demo only).
 */
import { useEffect, useRef, useState } from 'react'
import { FEATURES } from '~/config/mode'
import { Icon } from './ui'

export interface ViewerItem { id: string; src: string | null; alt?: string; liked?: boolean; galleryId?: string; mediaType?: string }

export function Viewer({ items, start, onClose, onLike, onTextMe }: {
  items: ViewerItem[]; start: number; onClose: () => void
  onLike?: (item: ViewerItem) => void; onTextMe?: (item: ViewerItem) => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const track = useRef<HTMLDivElement>(null)
  const [i, setI] = useState(start)
  const [hint, setHint] = useState('')
  useEffect(() => {
    const d = ref.current
    if (d && !d.open) d.showModal()
    requestAnimationFrame(() => { if (track.current) track.current.scrollTo({ left: start * track.current.clientWidth, behavior: 'instant' as ScrollBehavior }) })
  }, [])
  const step = (d: number) => track.current?.scrollBy({ left: d * track.current.clientWidth, behavior: 'smooth' })
  const cur = items[i]

  return (
    <dialog className="viewer" ref={ref} aria-label="Photo viewer" onClose={onClose}
      onKeyDown={(e) => { if (e.key === 'ArrowRight') step(1); else if (e.key === 'ArrowLeft') step(-1) }}>
      <div className="v-top"><span className="v-hint" role="status">{hint}</span>
        <button className="icon-btn" type="button" aria-label="Close viewer" onClick={() => ref.current?.close()}><Icon id="i-close" /></button></div>
      <button className="icon-btn v-arrow l" type="button" aria-label="Previous photo" onClick={() => step(-1)}><Icon id="i-back" /></button>
      <div className="v-track" ref={track} tabIndex={0}
        onScroll={(e) => { const el = e.currentTarget; const n = Math.round(el.scrollLeft / el.clientWidth); if (n !== i && items[n]) setI(n) }}>
        {items.map((it) => (
          <div className="v-slide" key={it.id}>
            {it.mediaType === 'video' && it.src ? <video src={it.src} controls controlsList="nodownload" playsInline style={{ maxWidth: '100%', maxHeight: 'calc(100dvh - 200px)' }} />
              : it.mediaType === 'audio' && it.src ? <audio src={it.src} controls controlsList="nodownload" />
              : it.src ? <img src={it.src} alt={it.alt || 'Photo'} decoding="async" /> : null}
          </div>
        ))}
      </div>
      <button className="icon-btn v-arrow r" type="button" aria-label="Next photo" onClick={() => step(1)}><Icon id="i-back" style={{ transform: 'scaleX(-1)' }} /></button>
      <div className="v-bottom">
        <div className="v-actions">
          {onLike && cur?.galleryId ? (
            <button className="v-act" type="button" aria-pressed={!!cur.liked} aria-label={cur.liked ? 'Remove heart' : 'Heart this photo'} onClick={() => onLike(cur)}><Icon id="i-heart" /></button>
          ) : <span />}
          {onTextMe && <button className="v-tell" type="button" onClick={() => { ref.current?.close(); onTextMe(cur) }}><Icon id="i-phone" />Text me</button>}
          {FEATURES.photoComments ? (
            <button className="v-act" type="button" aria-label="Comments" onClick={() => { setHint('Comments are preview only'); setTimeout(() => setHint(''), 2200) }}><Icon id="i-chat" /></button>
          ) : <span />}
        </div>
      </div>
    </dialog>
  )
}
